import { useMemo, useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@understory-io/leaf/button';
import { IconButton } from '@understory-io/leaf/icon-button';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { ModalDialog, Body, Footer } from '~/ui/modal-dialog/modal-dialog';
import { SelectTimeError, EventCard } from '~/ui/event-card';
import type { SeatCounterVisibility } from '~/ui/availability-label/availability-label';
import { buildTicketSummary } from '~/lib/ticket-summary';
import { FilterIcon } from '~/ui/icons/filter';
import { TicketsField } from '~/ui/tickets-field';
import type { TicketVariantOption } from '~/ui/tickets-field';
import { DateStepper, type AvailabilityFilters } from '~/ui/date-stepper';
import { isBookableStatus, type DateGroup } from '~/domain/availability/types';
import { resolveSelectedEventId } from '~/domain/availability/sole-bookable-event';
import {
  AvailabilityUnavailableNotice,
  type AvailabilityUnavailableNoticeProps,
} from '~/ui/availability-unavailable';

type BookingDialogProps = {
  // ---- Tickets (controlled) ----
  /** `null` while the deferred ticket data loads — renders a skeleton in
   *  place of the tickets field and disables Continue. */
  ticketVariants: TicketVariantOption[] | null;
  ticketQuantities: Record<string, number>;
  onTicketQuantitiesChange: (next: Record<string, number>) => void;
  /** Step for seat-variant pickers (the experience's multiple-of rule). */
  seatStep?: number;
  /** The experience's max tickets per booking (see `TicketsField`). */
  maxSeats?: number;
  /** The storefront's seat counter setting for the event list. */
  seatCounterVisibility?: SeatCounterVisibility;

  // ---- From-date / calendar (controlled) ----
  /** Current "from" date used by the loader to fetch events. */
  fromDate: string;
  onFromDateChange: (date: string) => void;
  /** Experience ID passed to DateStepper for self-fetching availability. */
  experienceId: string;
  /** Availability filters passed to DateStepper. */
  filters?: AvailabilityFilters;
  /** Date the calendar should anchor on when first opened. */
  initialFocusDate?: string | null;

  // ---- Filters ----
  /** Whether to render the Filters button. Owner should hide it when no
   *  axis has more than one option to choose from. */
  showFilterButton?: boolean;
  /** Number of currently-applied filter values across all axes. Renders a
   *  small indicator dot on the trigger when ≥ 1. */
  activeFilterCount?: number;
  /** Opens the filter dialog. Owner controls the modal (route-coupled). */
  onOpenFilters?: () => void;

  /** Ticket-selection error (no tickets / min / max / multiple-of), rendered
   *  under the TicketsField exactly like the checkout dates step. When present
   *  the selection is invalid and Continue won't proceed — it stays hidden
   *  until the visitor's first blocked Continue attempt, then tracks the live
   *  validation state. `null`/absent means the tickets are valid. */
  ticketError?: ReactNode;

  // ---- Events (from loader) ----
  /** `null` while the deferred event data loads — renders time-slot skeletons
   *  and disables Continue. */
  groups: DateGroup[] | null;
  /** Set when the events could not be checked: the try-again notice stands
   *  in for the event list (an empty day would read as nothing to book) and
   *  Continue stays disabled. */
  eventsUnavailable?: AvailabilityUnavailableNoticeProps | null;
  /** Seed selection (e.g. from basket.eventId), honoured while that event is
   *  one of the day's own — a basket event from another date, or one the
   *  active filters exclude, is ignored so the day still pre-selects its only
   *  bookable slot. Overridden by the visitor's pick. */
  defaultSelectedEventId?: string | null;

  // ---- Route-action exits ----
  onContinue: (eventId: string) => void;
  /** Shows the Continue button's busy state while the action is in flight. */
  continueBusy?: boolean;
  onClose: () => void;
};

export type { BookingDialogProps };

/**
 * The dialog is fully controlled by the caller — in production that's the
 * route component owning `loaderData`. Only the visitor's own time-slot pick
 * stays in local state, committed to the basket on Continue.
 *
 * Events are scoped to the calendar's currently-selected `fromDate`. The
 * calendar dots (driven by `DateStepper`'s self-fetched availability) hint
 * where other bookable dates live; picking a different day refetches via
 * the `fromDate` round-trip.
 */
export function BookingDialog({
  ticketVariants,
  ticketQuantities,
  onTicketQuantitiesChange,
  seatStep,
  maxSeats,
  seatCounterVisibility,
  fromDate,
  onFromDateChange,
  experienceId,
  filters,
  initialFocusDate = null,
  showFilterButton = false,
  activeFilterCount = 0,
  onOpenFilters,
  ticketError,
  groups,
  eventsUnavailable = null,
  defaultSelectedEventId = null,
  onContinue,
  continueBusy = false,
  onClose,
}: BookingDialogProps) {
  const intl = useIntl();

  const ticketsLoading = ticketVariants === null;
  const eventsLoading = groups === null;

  // Only events on the calendar's selected day. The calendar dots advertise
  // other bookable dates; clicking one refetches via `onFromDateChange`.
  const selectedGroup = useMemo(
    () => (groups ?? []).find((g) => g.date === fromDate) ?? null,
    [groups, fromDate]
  );

  // The visitor's own pick, tagged with the day it was made on so it can't
  // leak onto another day's list. Everything else — the caller's default
  // (basket / deep-link) and the single-slot pre-selection — is derived, which
  // is what lets a deferred or refiltered event set seed itself with no effect
  // to re-run.
  const [pick, setPick] = useState<{ date: string; eventId: string } | null>(
    null
  );
  const selectedEventId = useMemo(
    () =>
      resolveSelectedEventId(
        selectedGroup?.events ?? [],
        pick?.date === fromDate ? pick.eventId : defaultSelectedEventId
      ),
    [selectedGroup, fromDate, pick, defaultSelectedEventId]
  );

  // Ticket errors usually apply from the first render (the cart starts empty,
  // breaking the min rule), but surfacing them before the visitor has tried to
  // continue reads as scolding. Latch on the first blocked Continue attempt;
  // from then on the messages track the live validation state.
  const [showErrors, setShowErrors] = useState(false);

  // All ticket errors (no tickets / min / max / multiple-of) render under the
  // TicketsField, mirroring the checkout dates step. `ticketError == null`
  // means the selection is valid.
  const visibleTicketError = showErrors ? ticketError : undefined;

  const ticketSummary = useMemo(() => {
    const tickets = Object.entries(ticketQuantities).map(
      ([variantId, quantity]) => ({ variantId, quantity })
    );
    const summary = buildTicketSummary(tickets, ticketVariants ?? [], {
      include: 'all',
    });
    return (
      summary ||
      intl.formatMessage({
        defaultMessage: 'Add tickets',
        description: 'TicketsField placeholder when no tickets are selected',
      })
    );
  }, [ticketQuantities, ticketVariants, intl]);

  const hasEventsForSelectedDate = (selectedGroup?.events.length ?? 0) > 0;

  // The selected event as the visitor sees it, on the day on screen. When
  // opened from the edit flow the basket's previously-selected event stays
  // selected even if the new ticket count exceeds its capacity; the visual
  // error is rendered inline on the slot itself, so here it only has to gate
  // Continue. `ticketError` covers the global min/max/multiple-of rules, so
  // per-event availability is checked separately.
  const selectedEvent = useMemo(
    () =>
      (selectedGroup?.events ?? []).find(
        (e) => e.eventId === selectedEventId
      ) ?? null,
    [selectedGroup, selectedEventId]
  );
  const selectionBookable =
    selectedEvent !== null &&
    isBookableStatus(selectedEvent.availability.status);

  // Every gate a Continue needs to pass: loaded data, valid non-empty
  // tickets, and a bookable event chosen on the day being shown.
  const canContinue =
    !ticketsLoading &&
    !eventsLoading &&
    ticketError == null &&
    selectionBookable;

  // Valid tickets but no bookable time chosen (none picked, or the pre-selected
  // one has since sold out) — prompt for a time rather than leaving Continue a
  // dead control.
  const showSelectEventError =
    showErrors && ticketError == null && !selectionBookable;

  const filterButton = showFilterButton ? (
    <IconButton
      appearance="text"
      size="standard"
      onClick={() => onOpenFilters?.()}
      aria-label={
        activeFilterCount > 0
          ? intl.formatMessage(
              {
                defaultMessage: 'Filters ({count} active)',
                description:
                  'Accessible label on the booking dialog filter button when filters are applied',
              },
              { count: activeFilterCount }
            )
          : intl.formatMessage({
              defaultMessage: 'Filters',
              description:
                'Accessible label on the booking dialog filter button when no filters are applied',
            })
      }
      className={filterButtonStyles}
    >
      <FilterIcon />
      {activeFilterCount > 0 && (
        <span aria-hidden="true" className={filterActiveDotStyles} />
      )}
    </IconButton>
  ) : null;

  return (
    <ModalDialog
      title={
        <FormattedMessage
          defaultMessage="Who & when?"
          description="Booking dialog heading"
        />
      }
      actions={filterButton}
      onClose={onClose}
    >
      <Body>
        {ticketsLoading ? (
          <div className={skeletonFieldStyles} aria-hidden />
        ) : (
          <TicketsField
            summary={ticketSummary}
            variants={ticketVariants}
            quantities={ticketQuantities}
            seatStep={seatStep}
            maxSeats={maxSeats}
            onQuantitiesChange={onTicketQuantitiesChange}
            tone={visibleTicketError ? 'critical' : undefined}
            errorMessage={visibleTicketError}
          />
        )}

        <Stack gap="medium">
          <DateStepper
            experienceId={experienceId}
            date={fromDate}
            onDateChange={onFromDateChange}
            filters={filters}
            initialFocusDate={initialFocusDate}
          />

          {eventsLoading ? (
            <div className={skeletonSlotStyles} aria-hidden />
          ) : eventsUnavailable ? (
            <AvailabilityUnavailableNotice {...eventsUnavailable} />
          ) : hasEventsForSelectedDate ? (
            <Stack gap="small">
              {selectedGroup!.events.map((event) => (
                <EventCard
                  key={event.eventId}
                  sessions={event.sessions}
                  description={event.description}
                  locales={event.locales}
                  status={event.availability.status}
                  availableSeats={event.availability.availableSeats}
                  hideSeatCount={event.availability.hideSeatCount}
                  bookingMaxBelowSeatCount={
                    event.availability.bookingMaxBelowSeatCount
                  }
                  seatCounterVisibility={seatCounterVisibility}
                  selected={event.eventId === selectedEventId}
                  onClick={() =>
                    setPick({ date: fromDate, eventId: event.eventId })
                  }
                  // When a ticket error is showing it is authoritative — the
                  // per-slot "try reducing tickets" notice would contradict it
                  // (e.g. when the user has *fewer* tickets than the minimum).
                  hideCapacityNotice={visibleTicketError != null}
                />
              ))}
            </Stack>
          ) : (
            <Text style="body-small-regular" className={emptyDateStyles}>
              <FormattedMessage
                defaultMessage="No events on this date — try another."
                description="Empty state shown in the booking dialog when the selected date has no events"
              />
            </Text>
          )}
        </Stack>
      </Body>
      <Footer>
        {/* Ticket errors render under the TicketsField (above); the footer only
            carries the "pick a time" prompt, so no error shows twice. */}
        {showSelectEventError && <SelectTimeError />}
        <Button
          appearance="primary"
          size="large"
          busy={continueBusy}
          // Never disabled for validation — a blocked click reveals the reason
          // (too few/many tickets, or no time chosen) instead of leaving a dead
          // control. Only an in-flight submit, or deferred data still loading
          // or not checked, disables it.
          disabled={
            continueBusy ||
            ticketsLoading ||
            eventsLoading ||
            eventsUnavailable !== null
          }
          onClick={() => {
            if (!canContinue) {
              setShowErrors(true);
              return;
            }
            if (selectedEventId) onContinue(selectedEventId);
          }}
        >
          <FormattedMessage
            defaultMessage="Continue"
            description="Booking dialog Continue button"
          />
        </Button>
      </Footer>
    </ModalDialog>
  );
}

const filterButtonStyles = css({
  position: 'relative',
  color: 'content.secondary',
});

const filterActiveDotStyles = css({
  position: 'absolute',
  top: 'tiny',
  right: 'tiny',
  width: 'xsmall',
  height: 'xsmall',
  borderRadius: 'full',
  background: 'status.negative.bold',
});

const emptyDateStyles = css({
  color: 'content.secondary',
  paddingY: 'small',
});

// Placeholder blocks for the deferred sections, sized like the controls they
// stand in for so the dialog doesn't jump when the data arrives.
const skeletonFieldStyles = css({
  height: '[56px]',
  borderRadius: 'small',
  background: 'surface.neutral',
});

const skeletonSlotStyles = css({
  height: '[64px]',
  borderRadius: 'small',
  background: 'surface.neutral',
});
